import { useCallback, useState } from 'react';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useService } from '@xstate/react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import BackgroundSound from './BackgroundSound.Container';
import Screen from '../components/atoms/Screen';
import Map from '../components/atoms/Map';
import FogMapLayer from '../components/atoms/FogMapLayer';
import ConceptsOverlayMapLayer from '../components/molecules/ConceptsOverlayMapLayer.Container';
import PersonasMapLayer from '../components/molecules/PersonasMapLayer.Container';
import PersonasFeedbackMapLayer from '../components/molecules/PersonasFeedbackMapLayer.Container';
import ConceptsBarMapLayer from '../components/molecules/ConceptsBarMapLayer.Container';
import ScoreBarMapLayer from '../components/molecules/ScoreBarMapLayer.Container';
import ScoreChangeMapLayer from '../components/molecules/ScoreChangeMapLayer.Container';
import ScoreMapLayer from '../components/molecules/ScoreMapLayer.Container';
import TutorialBarLayer from '../components/molecules/TutorialBarLayer';
import SettingsDialog from '../components/organisms/SettingsDialog.Container';
import type { Persona } from '../data/Personas';
import Assets from '../assets/Assets';

import interpreter from '../state/service';
import { DIALOG_CLASSNAME, DIALOG_TIMEOUT } from '../utils/animation';

export interface StateProps {
	playthroughKey: string;
}

export interface DispatchProps {}

export interface OwnProps {}

type Props = StateProps & DispatchProps & OwnProps;

const GameBase: FunctionComponent<Props> = (
	props: PropsWithChildren<Props>
) => {
	const { playthroughKey, children } = props;
	const [state, send] = useService(interpreter);
	const [showSettings, setShowSettings] = useState(false);

	const isBackgroundBlurred: boolean =
		state.context.backgroundBlurred || showSettings;
	const isForegroundBlurred: boolean = showSettings;
	const fogLevel = state.context.fogLevel;
	const showPersonas: boolean = state.context.personasVisible;
	const showPersonaFeedback: boolean = state.context.feedbackVisible;
	const enabledPersonas: boolean = state.context.personasEnabled;
	const enabledPersonasIndex: number | null =
		state.context.personasEnabledIndex;
	const showScoreToolbar: boolean = state.context.toolbarScoreVisible;
	const showConceptsToolbar: boolean = state.context.toolbarConceptsVisible;
	const enabledConceptsToolbar: boolean = state.context.toolbarConceptsEnabled;
	const showScore: boolean = state.context.scoreVisible;
	const showTutorialToolbar = state.context.toolbarTutorialVisible;

	const openConcepts = useCallback(() => send('CONCEPTS'), [send]);
	const openPersona = useCallback(
		(persona: Persona) =>
			send('PERSONA_DETAILS', { personaName: persona.name }),
		[send]
	);
	const skipTutorial = useCallback(() => send('SKIP_TUTORIAL'), [send]);

	return (
		<>
			<BackgroundSound src={Assets.sounds.city} />
			<BackgroundSound src={Assets.sounds.music} />
			<Screen key={playthroughKey} blurred={isBackgroundBlurred}>
				{/* Background */}
				<Map />
				<ConceptsOverlayMapLayer />
				<TransitionGroup component={null}>
					{fogLevel === 'map' ? (
						<CSSTransition key="map-fog" classNames="fog" timeout={500}>
							<FogMapLayer />
						</CSSTransition>
					) : null}
					{/* Personas */}
					{showPersonas ? (
						<CSSTransition
							key="personas"
							classNames="personas-overlay"
							timeout={1_000}
						>
							<PersonasMapLayer
								onPersona={enabledPersonas ? openPersona : undefined}
								enabledPersonaIndex={
									enabledPersonasIndex != null
										? enabledPersonasIndex
										: undefined
								}
							/>
						</CSSTransition>
					) : null}
					{showPersonaFeedback ? (
						<CSSTransition key="feedback" classNames="feedback" timeout={0}>
							<PersonasFeedbackMapLayer />
						</CSSTransition>
					) : null}
					{showScore ? (
						<CSSTransition
							key="scores-fixed"
							classNames="scores-overlay"
							timeout={1_000}
						>
							<ScoreMapLayer />
						</CSSTransition>
					) : null}
					{fogLevel === 'personas' ? (
						<CSSTransition key="personas-fog" classNames="fog" timeout={500}>
							<FogMapLayer />
						</CSSTransition>
					) : null}
					{/* Toolbars */}
					{showConceptsToolbar ? (
						<CSSTransition
							key="concepts-toolbar"
							classNames="concepts-toolbar"
							timeout={1_000}
						>
							<ConceptsBarMapLayer
								onConcepts={enabledConceptsToolbar ? openConcepts : undefined}
							/>
						</CSSTransition>
					) : null}
					{showScoreToolbar ? (
						<CSSTransition
							key="score-toolbar"
							classNames="score-toolbar"
							in={showScoreToolbar}
							timeout={1_000}
						>
							<ScoreBarMapLayer onSettingsClick={() => setShowSettings(true)} />
						</CSSTransition>
					) : null}
					{showScore ? (
						<CSSTransition
							key="scores"
							classNames="scores-overlay"
							timeout={1_000}
						>
							<ScoreChangeMapLayer />
						</CSSTransition>
					) : null}
				</TransitionGroup>
			</Screen>
			<Screen key="foreground" blurred={isForegroundBlurred}>
				{children}
			</Screen>
			<TransitionGroup component={null}>
				{showTutorialToolbar ? (
					<CSSTransition
						key="tutorial-toolbar"
						classNames="tutorial-toolbar"
						timeout={1_000}
					>
						<TutorialBarLayer onSkip={skipTutorial} />
					</CSSTransition>
				) : null}
				{showSettings ? (
					<CSSTransition
						in={showSettings}
						key="settings"
						classNames={DIALOG_CLASSNAME}
						timeout={DIALOG_TIMEOUT}
					>
						<SettingsDialog onClose={() => setShowSettings(false)} />
					</CSSTransition>
				) : null}
			</TransitionGroup>
		</>
	);
};

export default GameBase;
